/**
 * useProjectTreeAutoRefresh — keep the Explorer file tree fresh when files
 * change outside the SPA (agent writes, external git pushes). Issue 5, 2026-07-14.
 *
 * BACKGROUND: projectStore.fetchTree only runs after LOCAL SPA operations, so a
 * file an agent/collaborator adds never appears until the user manually reloads.
 * The backend already ships half the mechanism: the Forgejo webhook writes a
 * one-shot `.vibe-cache-stale` sentinel and GET /projects/{id} returns +
 * consumes `forgejo_cache_stale` (routes/projects.py:1254). Nothing on the
 * frontend consumed it. This hook completes that path AND covers the case the
 * push-only sentinel misses (agents writing straight to the /workspace volume):
 *
 *   1. Poll GET /projects/{id} every POLL_MS while the tab is visible; refetch
 *      the tree when the sentinel is set (refreshTreeIfStale).
 *   2. On return-to-foreground (visibilitychange / focus), unconditionally
 *      refetch once — catches non-push volume writes made while the tab was
 *      backgrounded. Debounced so visibilitychange + focus (which both fire on
 *      the same foreground tick) don't double-fetch. Mirrors the onForeground
 *      precedent in AuthContext.tsx:111-131.
 *
 * expandedFolders survives a wholesale tree replace: node ids are uuid5(path)
 * (deterministic per path) and fetchTree never touches expandedFolders, so
 * expansion state is preserved for unchanged paths automatically.
 */
import { useEffect } from 'react';
import { useProjectStore } from '@/stores/projectStore';

const POLL_MS = 30_000;
const FOREGROUND_DEBOUNCE_MS = 5_000;

export function useProjectTreeAutoRefresh(): void {
  const projectId = useProjectStore((s) => s.currentProject?.id ?? null);
  const fetchTree = useProjectStore((s) => s.fetchTree);
  const refreshTreeIfStale = useProjectStore((s) => s.refreshTreeIfStale);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    let lastForegroundRefresh = 0;

    const interval = window.setInterval(() => {
      if (cancelled) return;
      // Don't poll a hidden tab — the foreground handler covers re-entry.
      if (document.visibilityState !== 'visible') return;
      void refreshTreeIfStale(projectId);
    }, POLL_MS);

    const onForeground = () => {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastForegroundRefresh < FOREGROUND_DEBOUNCE_MS) return;
      lastForegroundRefresh = now;
      void fetchTree(projectId);
    };
    document.addEventListener('visibilitychange', onForeground);
    window.addEventListener('focus', onForeground);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onForeground);
      window.removeEventListener('focus', onForeground);
    };
  }, [projectId, fetchTree, refreshTreeIfStale]);
}
